import { Button } from "components/Button";
import { Card } from "components/Card";
import { InputFormField } from "components/InputFormField";
import { useEffect, useState } from "react";
import { maskCPF } from "utils/masks";
import { useToast } from "hooks/useToast";
import { Menu } from "components/Menu";
import { menuListItems } from "assets/menu/menuList";
import { Header } from "components/Header";
import { Title } from "components/Title";
import { Row, Col } from "./styles";
import { v4 as uuid } from 'uuid';
import { apiV4 } from "services/api";
import { useFormik } from "formik";
import { SelectModified } from "components/SelectModified";
import { Banner } from "./banner";

interface InputsProps {
  env: Env;
  integration: string;
  cpf: string;
  email: string;
  totalSpent: number;
}

// O pedido de teste vai pelo api-v4 (proxy autenticado pelo JWT do backoffice),
// que guarda a API key da seller-integration e conhece as URLs de QA e produção.
const SELLER_INTEGRATION_ORDERS_PATH = '/api/internal/seller-integration/orders';

type Env = 'qa' | 'prod';

const describeProxyError = (error: any): string => {
  const payload = error?.response?.data?.error;
  if (!payload) {
    return String(error?.message ?? error);
  }
  const upstream = payload.upstream
    ? ` (upstream ${payload.upstream.status}: ${JSON.stringify(payload.upstream.body)})`
    : '';
  return `${payload.code}: ${payload.message}${upstream}`;
}

let apiJson = {
  id: "",
  date: "2021-10-15 15:31:00",
  platform: 0,
  sellerId: "1",
  partnerPayment: 1,
  bankEmissor: 1,
  cardFlag: 1,
  paymentType: 1,
  parcels: 1,
  totalSpent: 6197.10,
  deliveryTax: 10,
  deliveryType: 2,
  deliveryTime: "2021-09-10",
  consumer: {
    birthDate: "1990-03-07",
    cpf: "12345678910",
    email: "comprador.interno@tgroup.com.br",
    gender: "Masculino", 
    name: "Comprador Interno",
    phone: [
      {
        number: "11912345678"
      }
    ],
    zipcode: "05001000"
  },
  billing: {
    cpf: "12345678910",
    email: "comprador.interno@tgroup.com.br",
    gender: "Masculino",
    name: "Comprador Interno",
    phone: [
      {
        number: "11912345678"
      }
    ],
    zipcode: "05001000"
  },
  products: [
    {
      name: "Tênis Air Jordan 1 Mid Chicago Black Toe",
      quantity: 1,
      value: 2849.99,
      discount: 0,
      deliveryTax: 10,
      deliveryType: 16,
      deliveryTime: "2021-09-10",
      mktSaleId: "1234-1",
      sku: "12345-1",
      ean: "123456-1"
    },
    {
      name: "iPhone XR Apple 64GB Branco 6,1” 12MP",
      quantity: 1,
      value: 3347.07,
      discount: 0,
      deliveryTax: 10,
      deliveryType: 16,
      deliveryTime: "2021-09-10",
      mktSaleId: "1234-2",
      sku: "12345-2",
      ean: "123456-3"
    }
  ]
}

const initBannerJson = {
  orderID: '',
  orderDate: '',
  orderPlatform: '0',
  orderSellerID: '1',
  orderPartnerPayment: 0,
  orderBankEmissor: '0',
  orderCardFlag: '3',
  orderDeliveryTax: '20.00',
  orderDeliveryType: '2',
  orderDeliveryTime: '2021-10-01',
  orderPaymentType: '1',
  orderParcels: '12',
  orderTotalSpent: '1.00',
  consumerBirthDate: '1993-01-15',
  consumerCPF: '',
  consumerEmail: 'email@confi.com.vc',
  consumerGender: 'F',
  consumerName: 'Usuario Confi',
  consumerPhone: '5511922222222',
  consumerZipcode: '99999999',
  billingCPF: '',
  billingEmail: 'usuario@confi.com.vc',
  billingGender: 'F',
  billingName: 'Usuario Confi',
  billingPhone: '5511922222222',
  billingZipcode: '00000000',
  productName: 'Camiseta Vermelha P',
  productQuantity: '1',
  productValue: '200.00',
  productDiscount: '0.00',
  productDeliveryTax: '10.00',
  productDeliveryType: '1',
  productDeliveryTime: '01/10/2021',
  productMktSaleID: '0',
  productSKU: 'CVP200',
  productEAN: 'string',
}

const DEFAULT_INPUTS:InputsProps = {
  env: 'qa',
  integration: 'banner',
  cpf: '12345678910',
  email: 'comprador.interno@tgroup.com.br',
  totalSpent: 1.00
}

export const InternalStore: React.FC = () => {
  const [bannerJson, setBannerJson] = useState(initBannerJson)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastPurchaseId, setLastPurchaseId] = useState('');
  const {handleSubmit, handleChange, values: inputsValues} = useFormik({
    initialValues: DEFAULT_INPUTS,
    onSubmit: (values) => {
      submit(values);
    }
  });
  const {setToast} = useToast();
  const today = new Date();

  useEffect(() => {
    const script = document.createElement('script');
    
    script.src = "https://cdn.confi.com.vc/scripts/tagBanner.min.js?sellerId=1&lightbox=false";
    // script.src = "https://h1confbrblob001.blob.core.windows.net/scripts/tagBanner.min.js?sellerId=1&lightbox=true";
    script.id = 'getData';
    script.async = true;
  
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    }
  }, [bannerJson]);

  const submit = (values: InputsProps) => {
    const id = uuid();
    if (values.integration === 'api') {
      let deliveryTime = new Date();
  
      deliveryTime.setDate(today.getDate() + 10);
      apiJson.id = id;
      apiJson.consumer.cpf = values.cpf.replace(/\D/g, '');
      apiJson.billing.cpf = values.cpf.replace(/\D/g, '');
      apiJson.consumer.email = values.email;
      apiJson.billing.email = values.email;
      apiJson.totalSpent = values.totalSpent;
      apiJson.date = today.toISOString();
      apiJson.deliveryTime = deliveryTime.toISOString();
  
      setIsSubmitting(true);
      apiV4.post(SELLER_INTEGRATION_ORDERS_PATH, { env: values.env, order: apiJson }).then((_) => {
        setToast({
          message: 'Compra criada',
          isActive: true,
          type: 'success',
          timeToRemoveToast: 3000
        });
      }).catch((error) => {
        setLastPurchaseId('');
        apiJson.id = '';
        setToast({
          message: `Erro! ${describeProxyError(error)}`,
          isActive: true,
          type: 'error',
          timeToRemoveToast: 3000
        });
      }).finally(() => {
        setIsSubmitting(false);
      })
    } else {
      let deliveryTime = new Date();
  
      deliveryTime.setDate(today.getDate() + 10);
      setBannerJson({
        ...bannerJson,
        orderID: id,
        consumerCPF: values.cpf.replace(/\D/g, ''),
        billingCPF: values.cpf.replace(/\D/g, ''),
        consumerEmail: values.email,
        billingEmail: values.email,
        orderTotalSpent: values.totalSpent.toString(),
        orderDate: `${today.toISOString().split('T')[0]} 00:00:00`,
        orderDeliveryTime: deliveryTime.toISOString().split('T')[0],
      })
    }
    setLastPurchaseId(id);
  }

  return (
    <>
      <Menu links={menuListItems}>
        <Header>
          <Title text="Loja Interna" elementHTML="h1" className="subtitle-medium" />
        </Header>
        <Card title="Loja Interna">
          <Row>
            <Col>
              <SelectModified 
                name="env" 
                value={inputsValues.env}
                label='Ambiente'
                options={[
                  {label: 'QA', value: 'qa'},
                  {label: 'Prod', value: 'prod'}
                ]}
                onChange={handleChange}
                block />
            </Col>
            <Col>
            <SelectModified 
                name="integration" 
                value={inputsValues.integration}
                label='Integração'
                options={[
                  {label: 'Banner', value: 'banner'},
                  {label: 'Api', value: 'api'}
                ]}
                onChange={handleChange}
                block />
            </Col>
            <Col>
              <InputFormField name="cpf" block={true} label="CPF" value={inputsValues.cpf} mask={maskCPF} onChange={handleChange} />
            </Col>
            <Col>
              <InputFormField name="email" block={true} label="E-mail" value={inputsValues.email} onChange={handleChange} />
            </Col>
            <Col>
              <InputFormField type={"number"} name="totalSpent" label="Subtotal" value={inputsValues.totalSpent?.toFixed(2)} onChange={handleChange} />
            </Col>
          </Row>
          {lastPurchaseId !== '' ? `Id da última compra cadastrada: ${lastPurchaseId}`: ''}
          <br/>
          {lastPurchaseId !== '' ? `Recarregue a página para testar novamente o banner`: ''}
          {
            inputsValues.integration === 'banner' && lastPurchaseId.length > 0
              ? <div id="banner-section">
                  <Banner json={bannerJson}/>
                </div>
              : null
          }
          <Button label="Enviar" typeBtn="blue" load={isSubmitting} onClick={handleSubmit} />
        </Card>
      </Menu>
    </>
  );
}